import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, type DataTableColumn } from '@wundo/ui';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { fetchUserSummary, listBroadcasts } from '@/lib/broadcast.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import type { BroadcastHistoryItem } from '@/types/broadcast.ts';
import { BroadcastDetailDialog } from './BroadcastDetailDialog.tsx';
import { userSummaryName } from './userSummaryName.ts';

/** Who sent each broadcast on this page, by id. */
async function loadSenderNames(ids: string[]): Promise<Record<string, string>> {
  const users = await Promise.all(ids.map(fetchUserSummary));
  return Object.fromEntries(users.map((user) => [user.id, userSummaryName(user)]));
}

/**
 * Every broadcast that has been sent, newest first.
 *
 * The rows carry sender ids, so the names arrive after the table does — the
 * head of the id stands in until they land, which is what the record itself
 * says and never leaves a blank column.
 */
export function HistoryTab() {
  const [page, setPage] = useState(1);
  const [reading, setReading] = useState<BroadcastHistoryItem | null>(null);

  const broadcasts = useQuery({
    queryKey: ['broadcasts', page],
    queryFn: () => listBroadcasts(page, PAGE_SIZE),
  });

  const rows = broadcasts.data?.data ?? [];
  const senderIds = [...new Set(rows.map((broadcast) => broadcast.sent_by))].sort();

  const senders = useQuery({
    queryKey: ['broadcastSenders', senderIds],
    queryFn: () => loadSenderNames(senderIds),
    enabled: senderIds.length > 0,
  });

  const columns: DataTableColumn<BroadcastHistoryItem>[] = [
    { key: 'template_name', header: 'Template' },
    {
      key: 'sent_by',
      header: 'Sent by',
      render: (broadcast) =>
        senders.data?.[broadcast.sent_by] ?? broadcast.sent_by.slice(0, 8),
    },
    { key: 'total', header: 'Total', align: 'end' },
    {
      key: 'sent',
      header: 'Sent',
      align: 'end',
      render: (broadcast) => (
        <span className='font-semibold text-success'>{broadcast.sent}</span>
      ),
    },
    {
      key: 'failed',
      header: 'Failed',
      align: 'end',
      render: (broadcast) => (
        <span
          className={
            broadcast.failed > 0 ? 'font-semibold text-danger' : 'text-ink-muted'
          }
        >
          {broadcast.failed}
        </span>
      ),
    },
    {
      key: 'user_ids',
      header: 'Users',
      nowrap: true,
      render: (broadcast) =>
        `${broadcast.user_ids.length} user${broadcast.user_ids.length !== 1 ? 's' : ''}`,
    },
    {
      key: 'created_at',
      header: 'Date',
      nowrap: true,
      render: (broadcast) => formatDateTime(broadcast.created_at),
    },
  ];

  return (
    <>
      <DataTable
        label='Broadcasts'
        minWidth='lg'
        columns={columns}
        rows={rows}
        rowKey={(broadcast) => broadcast.id}
        onRowClick={(broadcast) => setReading(broadcast)}
        loading={broadcasts.isPending}
        error={broadcasts.isError ? 'Failed to load the broadcast history' : undefined}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: broadcasts.data?.meta.totalItems ?? 0,
        }}
        onPageChange={setPage}
        emptyState={
          <EmptyState
            title='No broadcasts yet'
            description='Sent broadcasts and how they went are listed here.'
          />
        }
      />

      <BroadcastDetailDialog broadcast={reading} onClose={() => setReading(null)} />
    </>
  );
}
